/** "Your Deposit" report — MSC numbers only: Revenue − MSC cost = Net residual × revenue share = Your deposit. */
import React from 'react';
import { Equal, Minus, X } from 'lucide-react';
import { money } from '../finance/financeUi';

const Figure = ({ label, value, testid, strong }) => (
  <div className={`min-w-[120px] flex-1 rounded-xl border p-4 ${strong ? 'border-emerald-500/40 bg-emerald-500/10' : 'bg-background'}`} data-testid={testid}>
    <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`mt-1 font-semibold tabular-nums ${strong ? 'text-3xl text-emerald-700' : 'text-xl'}`}>{value}</p>
  </div>
);
const Op = ({ icon: Icon }) => <Icon className="hidden h-5 w-5 shrink-0 text-muted-foreground sm:block" />;

export function DepositEquation({ totals, split }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="deposit-equation">
      <Figure label="Revenue" value={money(totals.revenue)} testid="deposit-revenue" /><Op icon={Minus} />
      <Figure label="MSC cost" value={money(totals.msc_cost)} testid="deposit-msc-cost" /><Op icon={Equal} />
      <Figure label="Net residual" value={money(totals.net_residual)} testid="deposit-net" /><Op icon={X} />
      <Figure label="Your share" value={`${split}%`} testid="deposit-split" /><Op icon={Equal} />
      <Figure label="Your deposit" value={money(totals.deposit)} testid="deposit-total" strong />
    </div>
  );
}

export function DepositReport({ a }) {
  return (
    <div className="space-y-4" data-testid={`deposit-report-${a.analysis_id}`}>
      <DepositEquation totals={a.totals} split={a.split_pct} />
      <p className="text-xs text-muted-foreground">{a.totals.merchants} merchants · {money(a.totals.volume)} volume · {a.totals.txn_count.toLocaleString()} transactions · priced on MSC {a.platform} costs{a.period ? ` · ${a.period}` : ''}</p>
      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full min-w-[620px] text-left text-sm">
          <thead className="border-b bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground"><tr><th className="px-3 py-2">Merchant</th><th className="px-3 py-2 text-right">Volume</th><th className="px-3 py-2 text-right">Revenue</th><th className="px-3 py-2 text-right">MSC cost</th><th className="px-3 py-2 text-right">Net residual</th><th className="px-3 py-2 text-right">Deposit</th></tr></thead>
          <tbody>{a.rows.map((r, i) => (
            <tr key={i} className="border-b last:border-0"><td className="px-3 py-2">{r.merchant_name || '—'}<span className="block text-xs text-muted-foreground">{r.mid}</span></td><td className="px-3 py-2 text-right tabular-nums">{money(r.volume)}</td><td className="px-3 py-2 text-right tabular-nums">{money(r.revenue)}</td><td className="px-3 py-2 text-right tabular-nums">{money(r.msc_cost)}</td><td className="px-3 py-2 text-right tabular-nums">{money(r.net_residual)}</td><td className="px-3 py-2 text-right font-medium tabular-nums">{money(r.deposit)}</td></tr>
          ))}</tbody>
        </table>
      </div>
      <details className="text-xs text-muted-foreground"><summary className="cursor-pointer">How MSC cost is calculated</summary>
        <ul className="mt-2 space-y-1">{a.cost_lines.map((c) => <li key={c.label}>{c.label}: {c.unit === 'bps' ? `${c.amount} bps of volume` : c.unit === 'per_transaction' ? `${money(c.amount)} per transaction` : `${money(c.amount)} per merchant per month`}</li>)}</ul>
      </details>
    </div>
  );
}
